import memoize from 'memoize-one';

import { getIframeObservable_DO_NOT_USE } from '@atlassian/embedded-confluence/getIframeObservable';
import type { IframePassThroughProps } from '@atlassian/embedded-confluence/IframeElementType';

import { EDIT_PAGE_EMBED, VIEW_PAGE, WHITEBOARD_BOARD } from '@confluence/named-routes';

const REQUIRED_EMBEDDED_CONFLUENCE_HEADER_KEYS: (keyof EmbeddedConfluenceHeaderValues)[] = [
	'contentId',
	'parentProduct',
	'spaceKey',
];
// Although parentProductContentContainerId is optional, it is still required for unlicensed access.
const OPTIONAL_EMBEDDED_CONFLUENCE_HEADER_KEYS: (keyof EmbeddedConfluenceHeaderValues)[] = [
	'parentProductContentContainerId',
	'parentProductContentId',
];
export const HEADER_KEY = 'x-atl-embeddablepage-entityids';

type EmbeddedConfluenceHeaderValues = Pick<
	IframePassThroughProps,
	'parentProduct' | 'parentProductContentId' | 'parentProductContentContainerId'
> & {
	contentId: string;
	spaceKey: string;
};

export type EmbeddedConfluenceHeader = { [HEADER_KEY]: string };

/*
 * This function will return a custom header string for embedded Confluence.
 * It will parse the 'contentId' and 'spaceKey' only from the Embedded View/Embedded Edit URL. This to restrict so unlicensed user can only access EP view / edit page.
 * For the remaining required fields, it will either retrieve them from the iframe's observableObject or from URL query params.
 * The query params are used only for internal testing outside of the Parent Product context and for SSR (during SSR, observable object will not exist).
 * NOTE: 4/30/2021 Embedded Confluence does not support blogs. Once we support them, we need to add the route here.
 * NOTE: 10/13/2023 All embedded Confluence request should have the custom header, so that it works on other first party parent product's custom domains.
 */
export const getEmbeddedConfluenceHeader = (location?: {
	href: string;
}): EmbeddedConfluenceHeader | undefined => {
	if (!location) {
		location = window.location;
	}

	const url = location?.href ?? '';
	return getEmbeddedConfluenceHeaderFromURL(url);
};

const getEmbeddedConfluenceHeaderFromURL = memoize((url: string) => {
	const match = VIEW_PAGE.match(url) || EDIT_PAGE_EMBED.match(url) || WHITEBOARD_BOARD.match(url);

	if (!match) return;

	const observableObject = getIframeObservable_DO_NOT_USE();

	const headerValue = [
		...REQUIRED_EMBEDDED_CONFLUENCE_HEADER_KEYS,
		...OPTIONAL_EMBEDDED_CONFLUENCE_HEADER_KEYS,
	].reduce((value, key) => {
		value[key] = observableObject?.object?.[key] || match.params?.[key] || match.query?.[key];
		return value;
	}, {} as EmbeddedConfluenceHeaderValues);

	const isValidHeader = REQUIRED_EMBEDDED_CONFLUENCE_HEADER_KEYS.every((key) =>
		Boolean(headerValue[key]),
	);

	return isValidHeader ? { [HEADER_KEY]: JSON.stringify(headerValue) } : undefined;
});
